import type { BatchUpdateAreasDto, BatchUpdateAreasResult } from './types/resource-area';
import { createAppRequest } from "@/services/request";
import { extractDetail } from "@/utils/apiResponse";
import type { CrmScopedViewMode } from "@/modules/zy-crm/utils/crm-view";
import type { ApiRequestOptions } from "./types/common";
import type {
  BatchDeleteContactDto,
  BatchUpdateContactStatusDto,
  ContactDrawerProfile,
  ContactDrawerSummaryPanel,
  CreateContactTeamMemberDto,
  ContactItem,
  ContactPageResult,
  ContactQueryParams,
  CreateContactDto,
  UpdateContactDto,
} from "./types/contact";

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm/contacts';

type ContactDetailScopeOptions = {
  viewMode?: CrmScopedViewMode;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object";

const normalizeContactPage = (resp: unknown): ContactPageResult => {
  if (!isObject(resp)) {
    throw new Error("联系人列表返回结构异常，请检查 API 地址或代理配置");
  }

  const root = resp as Record<string, unknown>;
  const candidates: unknown[] = [
    root,
    root.data,
    root.result,
    root.payload,
    (root.response as Record<string, unknown> | undefined)?.data,
  ];

  for (const candidate of candidates) {
    if (!isObject(candidate)) {
      continue;
    }
    const pageObj = candidate as Record<string, unknown>;
    const hasList = Array.isArray(pageObj.list);
    const hasItems = Array.isArray(pageObj.items);
    const hasDataArray = Array.isArray(pageObj.data);
    const data: unknown[] = hasList
      ? (pageObj.list as unknown[])
      : hasItems
        ? (pageObj.items as unknown[])
        : hasDataArray
          ? (pageObj.data as unknown[])
          : [];
    const totalRaw = pageObj.total ?? pageObj.count ?? data.length;
    const pageRaw = pageObj.page ?? pageObj.current ?? 1;
    const pageSizeRaw =
      (pageObj.pageSize ?? pageObj.limit ?? data.length) || 20;

    if (
      hasList ||
      hasItems ||
      hasDataArray ||
      pageObj.total !== undefined ||
      pageObj.count !== undefined
    ) {
      return {
        list: data as ContactItem[],
        total: Number(totalRaw) || 0,
        page: Number(pageRaw) || 1,
        pageSize: Number(pageSizeRaw) || 20,
      };
    }
  }

  throw new Error("联系人列表返回结构异常，请检查 API 地址或代理配置");
};

const unwrapContactDetail = (resp: unknown): ContactItem => {
  if (resp && typeof resp === "object") {
    const obj = resp as Record<string, unknown>;
    const candidates = [
      obj,
      obj.data,
      obj.result,
      obj.payload,
      (obj.response as Record<string, unknown> | undefined)?.data,
    ];
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== "object") {
        continue;
      }
      const record = candidate as Record<string, unknown>;
      if (typeof record.uuid === "string") {
        return candidate as ContactItem;
      }
    }
  }
  throw new Error("联系人详情返回结构异常");
};

export async function queryContacts(
  params: ContactQueryParams,
  options?: ApiRequestOptions
) {
  const resp = await request<unknown>(BASE_PATH, {
    method: "GET",
    params: {
      ...params,
      tagUuids: Array.isArray(params.tagUuids)
        ? params.tagUuids.join(",")
        : params.tagUuids,
    },
    ...(options || {}),
  });
  return normalizeContactPage(resp);
}

export async function queryContactDetail(
  uuid: string,
  options?: ApiRequestOptions & ContactDetailScopeOptions
) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: "GET",
    params: {
      viewMode: options?.viewMode,
    },
    ...(options || {}),
  });
  const detail = unwrapContactDetail(resp);
  console.log("[crm-contact-detail-frontend]", {
    uuid,
    detail,
  });
  return detail;
}

export async function revealContactMobile(
  uuid: string,
  options?: ApiRequestOptions
) {
  return request<{ uuid: string; mobile?: string }>(`${BASE_PATH}/${uuid}/mobile`, {
    method: "GET",
    ...(options || {}),
  });
}

export async function queryContactDrawerProfile(
  uuid: string,
  options?: ApiRequestOptions & ContactDetailScopeOptions
) {
  const resp = await request<ContactDrawerProfile | { data?: ContactDrawerProfile }>(
    `${BASE_PATH}/${uuid}/drawer-profile`,
    {
      method: "GET",
      params: {
        viewMode: options?.viewMode,
      },
      ...(options || {}),
    }
  );
  const detail = extractDetail(resp) as ContactDrawerProfile;
  console.log("[crm-contact-drawer-profile-frontend]", {
    uuid,
    detail,
  });
  return detail;
}

export async function createContactTeamMember(
  uuid: string,
  payload: CreateContactTeamMemberDto,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteContactTeamMember(
  uuid: string,
  teamMemberUuid: string,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members/${teamMemberUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function createContact(
  payload: CreateContactDto,
  options?: ApiRequestOptions
) {
  return request<ContactItem>(BASE_PATH, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function updateContact(
  uuid: string,
  payload: UpdateContactDto,
  options?: ApiRequestOptions
) {
  return request<ContactItem>(`${BASE_PATH}/${uuid}`, {
    method: "PATCH",
    data: payload,
    ...(options || {}),
  });
}

export async function deleteContact(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${BASE_PATH}/${uuid}`, {
    method: "DELETE",
    ...(options || {}),
  });
}

export async function batchDeleteContacts(
  payload: BatchDeleteContactDto,
  options?: ApiRequestOptions
) {
  return request<void>(`${BASE_PATH}/batch-delete`, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function batchUpdateContactStatus(
  payload: BatchUpdateContactStatusDto,
  options?: ApiRequestOptions
) {
  return request<void>(`${BASE_PATH}/batch-status`, {
    method: "PATCH",
    data: payload,
    ...(options || {}),
  });
}

export async function restoreContact(
  uuid: string,
  options?: ApiRequestOptions
) {
  return request<void>(`${BASE_PATH}/${uuid}/restore`, {
    method: "PATCH",
    ...(options || {}),
  });
}

export interface ContactOverviewStatistics {
  total: number;
  dueToday: number;
  stale: number;
  decisionMakers: number;
  unbound: number;
}

export async function queryContactOverview(options?: ApiRequestOptions): Promise<ContactOverviewStatistics> {
  const response = await request<ContactOverviewStatistics>(`${BASE_PATH}/overview`, { method: 'GET', ...options });
  const data = extractDetail(response);
  if (!data || !Number.isFinite(Number(data.total))) throw new Error('概览统计返回结构异常');
  return data;
}

export async function batchUpdateContactAreas(payload: BatchUpdateAreasDto, options?: ApiRequestOptions) {
  return request<BatchUpdateAreasResult>(`${BASE_PATH}/batch-areas`, {
    method: 'PATCH', data: payload, ...(options || {}),
  });
}
